/**
 * economyBalance — équilibrage macro des marges (13/07/2026, demande user).
 *
 * Problème : la SOMME linéaire des bonus de CA (investissements jusqu'à
 * +125 % + rôles support jusqu'à +130 %, plafond +200 %) créait des marges
 * de 60-80 % irréalistes (le transport réel tourne à 2-6 % net) et une
 * courbe de développement exponentielle.
 *
 * Solution validée user (option c) :
 *   1. RENDEMENTS DÉCROISSANTS : le bonus global suit une courbe de
 *      saturation exponentielle plafonnée à +50 %. Les premiers
 *      investissements gardent quasiment tout leur effet (dérivée ≈ 1 en
 *      bas de courbe → dynamique rapide conservée), chaque bonus
 *      supplémentaire ajoute toujours quelque chose (incitatif conservé)
 *      mais de moins en moins.
 *   2. PRESSION CONCURRENTIELLE : au-delà de 50 % de marge nette (30 j
 *      glissants), les clients renégocient les tarifs des contrats
 *      CLASSIQUES (réduction progressive, max −25 %). Les contrats gagnés
 *      par APPEL D'OFFRES (`tenderWon`) sont EXEMPTÉS : leurs prix sont
 *      contractuels — les AO plus difficiles restent la voie vers les
 *      marges > 50 %.
 */

export const GLOBAL_BONUS_CAP = 0.50;
export const GLOBAL_BONUS_SCALE = 0.45;

/**
 * Courbe de saturation du bonus global de CA.
 * raw 0.10 → ~0.10 · raw 0.25 → ~0.21 · raw 0.50 → ~0.34 ·
 * raw 1.00 → ~0.45 · raw 2.50 → ~0.50 (asymptote).
 */
export function effectiveGlobalBonus(raw) {
  if (!raw || raw <= 0) return 0;
  return GLOBAL_BONUS_CAP * (1 - Math.exp(-raw / GLOBAL_BONUS_SCALE));
}

export const MARGIN_PRESSURE_THRESHOLD = 0.50; // marge nette 30 j glissants
export const MARGIN_PRESSURE_SLOPE = 0.8;      // 80 % de l'excès renégocié
export const MARGIN_PRESSURE_MAX_REDUCTION = 0.25;
// Garde-fous : pas de pression sans historique ni volume significatif.
export const MARGIN_PRESSURE_MIN_HISTORY_DAYS = 15;
export const MARGIN_PRESSURE_MIN_REVENUE = 8000;

/**
 * Marge nette sur les 30 derniers jours (treasuryHistory) → facteur de
 * pression appliqué aux revenus des contrats classiques (hors AO).
 */
export function computeMarginPressure(state) {
  const hist = (state.treasuryHistory || []).slice(-30);
  if (hist.length < MARGIN_PRESSURE_MIN_HISTORY_DAYS) {
    return { factor: 1, margin: 0, active: false };
  }
  const rev = hist.reduce((s, h) => s + (h.revenue || 0), 0);
  const exp = hist.reduce((s, h) => s + (h.expenses || 0), 0);
  if (rev < MARGIN_PRESSURE_MIN_REVENUE) return { factor: 1, margin: 0, active: false };
  const margin = (rev - exp) / rev;
  if (margin <= MARGIN_PRESSURE_THRESHOLD) return { factor: 1, margin, active: false };
  const reduction = Math.min(
    MARGIN_PRESSURE_MAX_REDUCTION,
    (margin - MARGIN_PRESSURE_THRESHOLD) * MARGIN_PRESSURE_SLOPE,
  );
  return { factor: 1 - reduction, margin, active: true };
}

// ── 18/07/2026 — Revalorisation des Appels d'Offres (demande user prod) ──
// « AO : +30 % de valeur globale ; et les AO bien inférieurs au
// fonctionnement courant de l'apprenant doivent garantir ≥ 20 % de
// rentabilité. » Appliqué à l'INSTANCIATION du tender (refreshTenders /
// injectAdHocTender) : le curseur de négociation ±30 % et les fourchettes
// concurrentes suivent automatiquement.
export const TENDER_VALUE_MULTIPLIER = 1.30;
export const TENDER_MIN_MARGIN_PCT = 0.20;

/**
 * Prix journalier plancher garantissant ≥ 20 % de marge brute sur les
 * coûts RÉELS moyens de la flotte du joueur (carburant + maintenance +
 * assurance + salaire chauffeur, prorata 22 j ouvrés). Retourne 0 si la
 * flotte/l'équipe est vide (pas de référence → pas de plancher).
 */
export function computeTenderFloorDailyPrice(state) {
  const vehicles = (state.vehicles || []).filter(v => v.status !== 'sold');
  const drivers = state.drivers || [];
  if (vehicles.length === 0 || drivers.length === 0) return 0;
  const avgVehicleDaily = vehicles.reduce((s, v) =>
    s + (v.dailyFuel || 0) + ((v.maintenancePerMonth || 0) / 22) + ((v.insurancePerMonth || 0) / 22), 0,
  ) / vehicles.length;
  const avgDriverDaily = drivers.reduce((s, d) =>
    s + ((d.employerCost || d.salaryGross || 0) / 22), 0,
  ) / drivers.length;
  const dailyCost = avgVehicleDaily + avgDriverDaily;
  if (dailyCost <= 0) return 0;
  return Math.ceil(dailyCost / (1 - TENDER_MIN_MARGIN_PCT));
}

/**
 * Patch de pricing appliqué au template d'AO au moment où il apparaît :
 * base × 1,30, remontée au plancher de rentabilité si besoin, fourchette
 * concurrente recalée proportionnellement.
 */
export function computeTenderPricingPatch(template, state) {
  const base = template?.baseDailyRevenue || 0;
  if (base <= 0) return {};
  const scaled = Math.round(base * TENDER_VALUE_MULTIPLIER);
  const floor = computeTenderFloorDailyPrice(state || {});
  const finalBase = Math.max(scaled, floor);
  const ratio = finalBase / base;
  const range = Array.isArray(template.competitorPriceRange)
    ? template.competitorPriceRange.map(x => Math.round(x * ratio))
    : template.competitorPriceRange;
  return { baseDailyRevenue: finalBase, competitorPriceRange: range };
}

// ── 25/07/2026 — Rentabilité des contrats RÉGULIERS (retour user prod) ──
// « Il faut encore booster les contrats réguliers qui ne sont quasi jamais
// rentables. » Objectif user : marge nette 5-20 % pour un contrat régulier
// correctement staffé. À l'instanciation, le revenu journalier est remonté
// au plancher garantissant REGULAR_MIN_MARGIN_PCT au-dessus du coût réel
// quotidien du binôme camion + chauffeur LE MOINS CHER capable d'exécuter
// le contrat (à défaut de binôme qualifié : moyenne de la flotte).
export const REGULAR_MIN_MARGIN_PCT = 0.12;

export function computeRegularFloorDaily(state, contract) {
  if (!state || !contract || contract.frequency !== 'régulier') return 0;
  const vehicles = (state.vehicles || []).filter(v => v.status !== 'sold');
  const drivers = state.drivers || [];
  if (vehicles.length === 0 || drivers.length === 0) return 0;
  // Lazy require — évite tout risque de cycle d'import data ↔ utils.
  // eslint-disable-next-line global-require
  const { vehicleSatisfiesContract, driverSatisfiesContract } = require('../utils/contractCompatibility');
  const vDaily = v => (v.dailyFuel || 0)
    + ((v.maintenancePerMonth || 0) / 22)
    + ((v.insurancePerMonth || 0) / 22);
  const dDaily = d => ((d.employerCost || d.salaryGross || d.salary || 0) / 22);
  let okV = [];
  let okD = [];
  try {
    okV = vehicles.filter(v => vehicleSatisfiesContract(v, contract).ok);
    okD = drivers.filter(d => driverSatisfiesContract(d, contract).ok);
  } catch (err) { /* compat check failed → fallback moyenne */ }
  const vCost = okV.length > 0
    ? Math.min(...okV.map(vDaily))
    : vehicles.reduce((s, v) => s + vDaily(v), 0) / vehicles.length;
  const dCost = okD.length > 0
    ? Math.min(...okD.map(dDaily))
    : drivers.reduce((s, d) => s + dDaily(d), 0) / drivers.length;
  const dailyCost = vCost + dCost;
  if (!(dailyCost > 0)) return 0;
  return Math.ceil(dailyCost / (1 - REGULAR_MIN_MARGIN_PCT));
}
